/**
 * useMergedProductsActions - Bulk-Aktionen + Datei-Upload + Modal-State für MergedProductsPage
 * Plan 012 - page-level actions hook (tracked exception: ~50 lines, komplex durch 4 Concerns)
 */

import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { bulkSetActive } from '@/lib/api/products'
import { useFileDropUpload } from '@/hooks/useFileDropUpload'
import type { ProductWithReadiness } from '@/types/products'

export function useMergedProductsActions(selectedIds: Set<string>, products: ProductWithReadiness[]) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const fileDropUpload = useFileDropUpload()
  const [quickViewProduct, setQuickViewProduct] = useState<ProductWithReadiness | null>(null)
  const [generateProduct, setGenerateProduct] = useState<ProductWithReadiness | null>(null)

  const bulkMutation = useMutation({
    mutationFn: ({ ids, active }: { ids: string[]; active: boolean }) => bulkSetActive(ids, active),
    onSuccess: (result, { active }) => {
      if (result.success) {
        toast.success(active ? 'Eingeblendet' : 'Ausgeblendet')
        qc.invalidateQueries({ queryKey: ['products'] })
      } else toast.error(result.error || 'Fehler')
    },
    onError: () => toast.error('Fehler beim Aktualisieren'),
  })

  const navigateToBatch = useCallback(() => {
    const ids = Array.from(selectedIds).slice(0, 50)
    const names = ids.map(id => encodeURIComponent(products.find(p => p.id === id)?.name ?? ''))
    navigate(`/v2/tools/generate/batch?products=${ids.join(',')}&names=${names.join(',')}&mode=matrix`)
  }, [selectedIds, products, navigate])

  const handleFileDrop = useCallback(
    (product: ProductWithReadiness, files: File[]) => fileDropUpload.uploadFiles(product.id, product.name, files),
    [fileDropUpload],
  )

  const handleCloseSortPanel = useCallback(
    () => { fileDropUpload.reset(); qc.invalidateQueries({ queryKey: ['products'] }) },
    [fileDropUpload, qc],
  )

  return {
    fileDropUpload, quickViewProduct, setQuickViewProduct,
    generateProduct, setGenerateProduct,
    bulkMutation, navigateToBatch, handleFileDrop, handleCloseSortPanel,
  }
}
